import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BarChart, ChevronDown, ChevronUp, UserPlus, Camera, ScanLine } from 'lucide-react';
import { io, Socket } from 'socket.io-client';
import { useTranslation } from 'react-i18next';
import API_BASE_URL, { WS_URL } from '../config/api';

interface Question {
  id: number;
  question_number: number;
  number_of_answers: number;
  correct_answer?: number;
  correct_answers?: string[]; // Pour les tests détaillés (array de lettres)
  part_id?: number;
  points?: number;
}

interface part {
  id: number;
  titre: string;
  ordre: number;
}

interface PartStats {
  correctCount: number;
  totalAnswered: number;
  totalQuestions: number;
  totalPoints: number;
  rawScore: number;
  convertedScore: number | null;
}

interface Student {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  correctCount: number;
  totalAnswered: number;
  percentage: number;
  partStats?: { [partId: number]: PartStats };
}

interface Test {
  id: number;
  title: string;
  total_questions: number;
  statut_libelle: string;
  type_id?: number;
}

interface Answers {
  [key: string]: number | string; // string pour les réponses multiples en JSON
}

const TestResults: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { testId } = useParams<{ testId: string }>();
  const [test, setTest] = useState<Test | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [parts, setparts] = useState<part[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [answers, setAnswers] = useState<Answers>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedparts, setExpandedparts] = useState<{ [key: number]: boolean }>({});

  // Convertir un nombre en lettre (1 -> A, 2 -> B, etc.)
  const numberToLetter = (num: number): string => {
    return String.fromCharCode(64 + num); // 65 = A
  };

  // Formater les bonnes réponses (pour la colonne "Bonne réponse")
  const formatCorrectAnswer = (question: Question): string => {
    // Pour les tests détaillés avec correct_answers (array de lettres)
    if (question.correct_answers && question.correct_answers.length > 0) {
      return question.correct_answers.join(' - ');
    }
    // Pour les tests simples avec correct_answer (nombre)
    if (question.correct_answer) {
      return numberToLetter(question.correct_answer);
    }
    return '-';
  };

  // Formater la réponse (simple ou multiple)
  const formatAnswer = (answer: number | number[] | undefined, numberOfAnswers?: number): string => {
    if (answer === undefined) return '-';

    if (Array.isArray(answer)) {
      // Si toutes les réponses sont sélectionnées, afficher "-"
      if (numberOfAnswers && answer.length === numberOfAnswers) {
        return '-';
      }
      // Réponses multiples séparées par des tirets
      return answer.map(a => numberToLetter(a)).join(' - ');
    }

    return numberToLetter(answer);
  };

  const fetchResults = useCallback(async (): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${testId}/results`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(t('tests.results.errorLoading'));
      }

      const data = await response.json();
      setTest(data.test);
      setQuestions(data.questions);
      setparts(data.parts || []);
      setStudents(data.students);
      setAnswers(data.answers);

      // Initialiser toutes les parts comme ouvertes par défaut
      const initialExpanded: { [key: number]: boolean } = {};
      if (data.parts) {
        data.parts.forEach((part: part) => {
          initialExpanded[part.id] = true;
        });
      }
      setExpandedparts(initialExpanded);

      setLoading(false);

    } catch (error) {
      console.error(t('common.error'), error);
      navigate('/my-tests');
    }
  }, [testId, navigate, t]);

  useEffect(() => {
    fetchResults();

    // Connexion Socket.io
    const socket: Socket = io(WS_URL, {
      transports: ['polling', 'websocket']
    });

    socket.on('connect', () => {
      console.log('✅', t('tests.results.websocket.connected'));
      // Rejoindre la room du test
      socket.emit('join-test', testId);
    });

    // Écouter les mises à jour de réponses
    socket.on('student-answer-updated', (data) => {
      console.log('📡', t('tests.results.websocket.answerReceived'), data);

      // Recharger les résultats complets
      fetchResults();
    });

    socket.on('disconnect', () => {
      console.log('❌', t('tests.results.websocket.disconnected'));
    });

    // Nettoyage à la déconnexion du composant
    return () => {
      socket.emit('leave-test', testId);
      socket.disconnect();
    };
  }, [testId, fetchResults, t]);

  const handleBack = (): void => {
    navigate('/my-tests');
  };

  const togglepart = (partId: number): void => {
    setExpandedparts(prev => ({
      ...prev,
      [partId]: !prev[partId]
    }));
  };

  const getAnswerForStudent = (questionId: number, studentId: number): number | number[] | undefined => {
    const key = `${questionId}_${studentId}`;
    const answer = answers[key];

    if (answer === undefined) return undefined;

    // Maintenant toutes les réponses sont des strings (TEXT dans la BDD)
    if (typeof answer === 'string') {
      // Essayer de parser comme JSON (réponses multiples)
      if (answer.startsWith('[')) {
        try {
          const parsed = JSON.parse(answer);
          return Array.isArray(parsed) ? parsed : [parseInt(answer)];
        } catch {
          // Si le parsing échoue, traiter comme un nombre
          return parseInt(answer);
        }
      } else {
        // Réponse simple stockée comme "1", "2", etc.
        return parseInt(answer);
      }
    }

    // Compatibilité avec l'ancien format (number)
    return answer;
  };

  const isCorrectAnswer = (questionId: number, studentId: number): boolean => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return false;

    const studentAnswer = getAnswerForStudent(questionId, studentId);
    if (studentAnswer === undefined) return false;

    // Pour les tests détaillés avec correct_answers (tableau de lettres)
    if (question.correct_answers && question.correct_answers.length > 0) {
      // Convertir les lettres en nombres (A=1, B=2, C=3, ...)
      const correctNumbers = question.correct_answers.map(letter =>
        letter.charCodeAt(0) - 64  // A=65, donc A-64=1, B-64=2, etc.
      ).sort();

      // Si c'est un tableau (réponses multiples)
      if (Array.isArray(studentAnswer)) {
        const sortedStudentAnswer = [...studentAnswer].sort();
        return JSON.stringify(correctNumbers) === JSON.stringify(sortedStudentAnswer);
      }

      // Si c'est une seule réponse
      return correctNumbers.length === 1 && correctNumbers[0] === studentAnswer;
    }

    // Pour les tests simples avec correct_answer (nombre unique)
    if (question.correct_answer) {
      if (Array.isArray(studentAnswer)) {
        return false; // Les tests simples ne devraient pas avoir de réponses multiples
      }
      return studentAnswer === question.correct_answer;
    }

    return false;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-white text-xl">{t('tests.results.loading')}</div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-white text-xl">{t('tests.results.notFound')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="text-gray-600 hover:text-gray-800 transition"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">{t('tests.results.resultsOf')} {test.title}</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-full mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          {/* Informations du test */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <BarChart className="w-8 h-8 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-800">
                  {t('tests.results.tableTitle')}
                </h2>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/camera-scanner/${testId}`)}
                  className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                >
                  <ScanLine className="w-5 h-5" />
                  Scanner Formulaires
                </button>
                <button
                  onClick={() => navigate(`/photo-validation/${testId}`)}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                >
                  <Camera className="w-5 h-5" />
                  {t('tests.results.scanPhoto')}
                </button>
                <button
                  onClick={() => navigate(`/manual-entry/${testId}`)}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                >
                  <UserPlus className="w-5 h-5" />
                  {t('tests.results.manualEntry')}
                </button>
              </div>
            </div>
            <p className="text-gray-600">
              {test.total_questions} {t('tests.results.questions')} • {students.length} {students.length > 1 ? t('tests.results.students') : t('tests.results.student')} • {t('tests.results.status')}: {test.statut_libelle}
            </p>
          </div>

          {students.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">
                {t('tests.results.noStudents')}
              </p>
            </div>
          ) : parts.length > 0 ? (
            // Affichage organisé par parts
            <div className="space-y-6">
              {parts.map((part) => {
                const partQuestions = questions.filter(q => q.part_id === part.id);
                const isExpanded = expandedparts[part.id];

                return (
                  <div key={part.id} className="border border-gray-300 rounded-lg overflow-hidden">
                    {/* En-tête de la part */}
                    <button
                      type="button"
                      onClick={() => togglepart(part.id)}
                      className="w-full bg-blue-600 px-4 py-3 hover:bg-blue-700 transition flex items-center justify-between"
                    >
                      <div className="text-left">
                        <h3 className="text-white font-bold text-lg">
                          {part.titre}
                        </h3>
                        <p className="text-blue-100 text-sm">
                          {partQuestions.length} {t('tests.results.questions')}
                        </p>
                      </div>
                      <div className="text-white">
                        {isExpanded ? (
                          <ChevronUp className="w-6 h-6" />
                        ) : (
                          <ChevronDown className="w-6 h-6" />
                        )}
                      </div>
                    </button>

                    {/* Tableau des résultats de la part */}
                    {isExpanded && (
                      <div className="overflow-x-auto">
                        <table className="border-collapse text-sm">
                          <thead>
                            <tr className="bg-gray-800">
                              <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white sticky left-0 bg-gray-800 z-10" style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}>
                                {t('tests.results.questionLabel')}
                              </th>
                              <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white bg-green-700" style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}>
                                {t('tests.results.correctAnswer')}
                              </th>
                              {students.map((student) => (
                                <th
                                  key={student.id}
                                  className="border border-gray-600 px-2 py-2 text-center font-semibold text-white"
                                  style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}
                                >
                                  <div className="flex flex-col items-center">
                                    {student.first_name || student.last_name ? (
                                      <>
                                        <span className="truncate max-w-[76px] text-xs">{student.first_name}</span>
                                        <span className="truncate max-w-[76px] text-xs">{student.last_name}</span>
                                      </>
                                    ) : (
                                      <span className="truncate max-w-[76px] text-xs">{student.username}</span>
                                    )}
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {partQuestions.map((question, index) => (
                              <tr
                                key={question.id}
                                className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`}
                              >
                                <td className="border border-gray-300 px-2 py-2 text-center font-semibold text-gray-700 sticky left-0 bg-inherit z-10" style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}>
                                  <div>Q{question.question_number}</div>
                                  {question.points && question.points > 1 && (
                                    <div className="text-xs text-gray-500">({question.points} pts)</div>
                                  )}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-center font-bold text-green-700 bg-green-50" style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}>
                                  {formatCorrectAnswer(question)}
                                </td>
                                {students.map((student) => {
                                  const studentAnswer = getAnswerForStudent(question.id, student.id);
                                  const isCorrect = isCorrectAnswer(question.id, student.id);

                                  return (
                                    <td
                                      key={student.id}
                                      className={`border border-gray-300 px-2 py-2 text-center font-semibold ${
                                        studentAnswer === undefined ? 'text-gray-400 bg-gray-100' :
                                        isCorrect ? 'text-green-700 bg-green-100' :
                                        'text-red-700 bg-red-100'
                                      }`}
                                      style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}
                                    >
                                      {formatAnswer(studentAnswer, question.number_of_answers)}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Statistiques globales en bas */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-bold text-gray-800 mb-3">{t('tests.results.globalStats')}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-blue-100">
                        <th className="px-3 py-2 text-center font-semibold text-gray-700" style={{ width: '200px', minWidth: '200px' }}>
                          {t('tests.results.studentColumn')}
                        </th>
                        {parts.map((part) => (
                          <th key={part.id} className="px-3 py-2 text-center font-semibold text-gray-700" style={{ width: '150px', minWidth: '150px' }}>
                            {part.titre}
                          </th>
                        ))}
                        <th className="px-3 py-2 text-center font-semibold text-gray-700 bg-blue-200" style={{ width: '150px', minWidth: '150px' }}>
                          {t('tests.results.totalScore')}
                        </th>
                        <th className="px-3 py-2 text-center font-semibold text-gray-700" style={{ width: '150px', minWidth: '150px' }}>
                          {t('tests.results.successRate')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student) => {
                        // Calculer le score total converti et les totaux en points
                        let totalConvertedScore = 0;
                        let hasConvertedScores = false;
                        let totalRawScore = 0;
                        let totalPossiblePoints = 0;

                        parts.forEach((part) => {
                          if (student.partStats && student.partStats[part.id]) {
                            const partStat = student.partStats[part.id];

                            if (partStat.convertedScore !== null) {
                              totalConvertedScore += partStat.convertedScore;
                              hasConvertedScores = true;
                            }

                            totalRawScore += partStat.rawScore;
                            totalPossiblePoints += partStat.totalPoints;
                          }
                        });

                        return (
                          <tr key={student.id} className="border-t border-blue-200">
                            <td className="px-3 py-2 text-center text-gray-700" style={{ width: '200px', minWidth: '200px' }}>
                              {student.first_name || student.last_name
                                ? `${student.first_name || ''} ${student.last_name || ''}`.trim()
                                : student.username
                              }
                            </td>
                            {parts.map((part) => {
                              const partStat = student.partStats?.[part.id];
                              return (
                                <td key={part.id} className="px-3 py-2 text-center text-gray-700" style={{ width: '150px', minWidth: '150px' }}>
                                  {partStat ? (
                                    <div>
                                      <div className="font-bold text-blue-700">
                                        {partStat.convertedScore !== null
                                          ? partStat.convertedScore
                                          : test?.type_id === 2
                                            ? `${partStat.rawScore}/${partStat.totalPoints}`
                                            : `${partStat.correctCount}/${partStat.totalQuestions}`
                                        }
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {partStat.convertedScore !== null
                                          ? test?.type_id === 2
                                            ? `${partStat.rawScore}/${partStat.totalPoints}`
                                            : `${partStat.correctCount}/${partStat.totalQuestions}`
                                          : `${partStat.correctCount}/${partStat.totalQuestions}`
                                        }
                                      </div>
                                    </div>
                                  ) : (
                                    <div>
                                      <div className="font-bold text-blue-700">0</div>
                                      <div className="text-xs text-gray-500">0/0</div>
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                            <td className="px-3 py-2 text-center font-bold text-blue-700 bg-blue-100" style={{ width: '150px', minWidth: '150px' }}>
                              {hasConvertedScores
                                ? totalConvertedScore
                                : test?.type_id === 2
                                  ? `${totalRawScore}/${totalPossiblePoints}`
                                  : `${student.correctCount}/${student.totalAnswered}`
                              }
                            </td>
                            <td className={`px-3 py-2 text-center font-bold ${
                              student.percentage >= 80 ? 'text-green-700' :
                              student.percentage >= 60 ? 'text-yellow-700' :
                              'text-red-700'
                            }`} style={{ width: '150px', minWidth: '150px' }}>
                              {student.percentage}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            // Affichage classique sans parts (rétrocompatibilité)
            <div className="overflow-x-auto">
              <table className="border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-800">
                    <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white sticky left-0 bg-gray-800 z-10" style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}>
                      {t('tests.results.questionLabel')}
                    </th>
                    <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white bg-green-700" style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}>
                      {t('tests.results.correctAnswer')}
                    </th>
                    {students.map((student) => (
                      <th
                        key={student.id}
                        className="border border-gray-600 px-2 py-2 text-center font-semibold text-white"
                        style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}
                      >
                        <div className="flex flex-col items-center">
                          {student.first_name || student.last_name ? (
                            <>
                              <span className="truncate max-w-[76px] text-xs">{student.first_name}</span>
                              <span className="truncate max-w-[76px] text-xs">{student.last_name}</span>
                            </>
                          ) : (
                            <span className="truncate max-w-[76px] text-xs">{student.username}</span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                  {/* Ligne des pourcentages */}
                  <tr className="bg-blue-50 font-bold">
                    <td className="border border-gray-300 px-2 py-2 text-center text-gray-700 sticky left-0 bg-blue-50 z-10 text-xs" style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}>
                      {t('tests.results.successRate')}
                    </td>
                    <td className="border border-gray-300 px-2 py-2 text-center text-gray-700" style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}>
                      -
                    </td>
                    {students.map((student) => (
                      <td
                        key={student.id}
                        className={`border border-gray-300 px-2 py-2 text-center font-bold text-sm ${
                          student.percentage >= 80 ? 'text-green-700 bg-green-100' :
                          student.percentage >= 60 ? 'text-yellow-700 bg-yellow-100' :
                          'text-red-700 bg-red-100'
                        }`}
                        style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}
                      >
                        {student.percentage}%
                        <div className="text-xs font-normal text-gray-600">
                          ({student.correctCount}/{student.totalAnswered})
                        </div>
                      </td>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {questions.map((question, index) => (
                    <tr
                      key={question.id}
                      className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`}
                    >
                      <td className="border border-gray-300 px-2 py-2 text-center font-semibold text-gray-700 sticky left-0 bg-inherit z-10" style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}>
                        <div>Q{question.question_number}</div>
                        {question.points && question.points > 1 && (
                          <div className="text-xs text-gray-500">({question.points} pts)</div>
                        )}
                      </td>
                      <td className="border border-gray-300 px-2 py-2 text-center font-bold text-green-700 bg-green-50" style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}>
                        {formatCorrectAnswer(question)}
                      </td>
                      {students.map((student) => {
                        const studentAnswer = getAnswerForStudent(question.id, student.id);
                        const isCorrect = isCorrectAnswer(question.id, student.id);

                        return (
                          <td
                            key={student.id}
                            className={`border border-gray-300 px-2 py-2 text-center font-semibold ${
                              studentAnswer === undefined ? 'text-gray-400 bg-gray-100' :
                              isCorrect ? 'text-green-700 bg-green-100' :
                              'text-red-700 bg-red-100'
                            }`}
                            style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}
                          >
                            {formatAnswer(studentAnswer, question.number_of_answers)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Légende */}
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-bold text-gray-800 mb-3">{t('tests.results.legend.title')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-green-100 border border-green-300 rounded"></div>
                <span className="text-gray-700">{t('tests.results.legend.correctAnswer')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-red-100 border border-red-300 rounded"></div>
                <span className="text-gray-700">{t('tests.results.legend.incorrectAnswer')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-gray-100 border border-gray-300 rounded"></div>
                <span className="text-gray-700">{t('tests.results.legend.noAnswer')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-green-50 border border-green-300 rounded"></div>
                <span className="text-gray-700">{t('tests.results.legend.correctColumn')}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TestResults;
